import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { CompanyApi } from "~/api/company-api";
import useUserState from "~/stores/user";
import type { Company } from "~/types/api";
import { useTranslation } from "react-i18next";
import { SoForm } from "../form/so-form";
import { FormFieldType } from "../form/fields";
import { toast } from "sonner";
import { isBulkFormData } from "~/utils/form-util";
import type {
  BulkFormData,
  CompanySelectorProps,
  FormOption,
} from "~/types/ui";
import { Loading } from "./loading";
import { NodeApi } from "~/api/node-api";
import useCodeMirrorState from "~/stores/code-mirror";
import { FeatureApi } from "~/api/feature-api";
import useFeaturesState from "~/stores/feature";
import useSettingState from "~/stores/setting";

export function CompanySelector(
  { onChange }: CompanySelectorProps = { onChange: () => {} },
) {
  // Fetch the company options
  const { t } = useTranslation();
  const [options, setOptions] = useState<FormOption[]>([]);
  const companyApi = new CompanyApi();
  const { data: companies, isLoading } = useQuery<Company[]>({
    queryKey: ["companies:all"],
    queryFn: async () => {
      const resp = await companyApi.getCompanies();
      if (!resp.ok || resp.data == undefined)
        throw new Error("Failed to fetch companies " + resp.error);

      setOptions(
        resp.data.map(
          (c) =>
            ({
              value: c.companyId,
              label: c.name,
            }) as FormOption<number>,
        ),
      );

      return resp.data;
    },
    staleTime: 0,
  });

  const queryClient = useQueryClient();
  const userState = useUserState();
  const settingState = useSettingState();
  const codeMirrorState = useCodeMirrorState();

  async function onSubmit(
    data: { selectedCompanyId: number | null } | BulkFormData<any>,
  ) {
    if (isBulkFormData(data)) return;

    data = data as { selectedCompanyId: number | null };
    if (data.selectedCompanyId !== null) {
      // Try to find the company from the "data"
      const foundCompany =
        companies?.find(
          (d: Company) => d.companyId === data.selectedCompanyId,
        ) ?? undefined;
      if (!foundCompany) {
        console.error("Company not found for ID:", data.selectedCompanyId);
        toast.error(t("company.selector.error"));
        onChange(null);
        return;
      }
      userState.setCompany(foundCompany);
      queryClient.invalidateQueries();
      onChange(foundCompany.companyId ?? null);
      toast.success(t("company.selector.changed", { name: foundCompany.name }));

      // Fetch the features
      await queryClient.fetchQuery({
        queryKey: ["company:features"],
        queryFn: async () => {
          const res = await new FeatureApi().getFeatures(
            foundCompany.companyId,
          );
          if (res.ok && res.data) {
            featuresState.setFeatures(res.data ?? []);
            return res.data;
          }
          return undefined;
        },
        staleTime: 0,
      });

      // Fetch the company settings
      await queryClient.fetchQuery({
        queryKey: ["company:settings"],
        queryFn: async () => {
          const res = await new CompanyApi().settings();
          if (res.ok && res.data) {
            settingState.setCompanySettings(res.data);
            return res.data;
          }
          return undefined;
        },
        staleTime: 0,
      });

      // Fetch nodes to setup autocorrect in codeMirror
      const nodesRes = await queryClient.fetchQuery({
        queryKey: ["nodes:all"],
        queryFn: async () => {
          const res = await new NodeApi().getAllNodes();
          if (!res.ok || !res.data) return [];
          return res.data;
        },
        staleTime: 0,
      });
      codeMirrorState.setAutocompletions(nodesRes);
    }
  }
  // Create the form structure
  const structure = {
    selectedCompanyId: {
      label: "company.selector.title",
      placeholder: "company.selector.placeholder",
      type: FormFieldType.COMBOBOX,
      required: true,
      options: options,
      default: userState.company?.companyId ?? 1,
    },
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <SoForm structure={structure} onSubmit={onSubmit} />
    </>
  );
}
